import React, { useState, useEffect, useRef } from 'react'
import { IProject, IResourcesUpdatedSocket, ITag } from 'types'
import { useLocation, useMatch, useNavigate } from 'react-router-dom'
import api from 'helpers/api'
import { INITIAL_PROJECT_INFORMATION, ProjectRoles } from 'app-constants'
import { setRedirectTo } from 'helpers/redirect'
import { useAuth } from 'hooks/auth'
import { useApplicationStore } from 'hooks/application'
import useSocket from 'hooks/useSocket'
import { subscribeSocketRoom } from 'helpers/socketClient'
import { ProjectContext } from 'hooks/projectContext'
import { HUB } from 'routes'
import { afterOwnWrites, ownWriteMark, readOverlappedOwnWrite, realtimeOrigin } from 'helpers/realtimeOrigin'
import { containersOf, createResourcesUpdatedBurst, mergeContainers, SidebarContainers } from 'helpers/resourcesUpdated'
import { applyContainerDiff, knowsContainers } from 'components/sidebar/components/folders/containerDiff'

/**
 * The sidebar fields of a hub response. A backend from before hub folders (say, after a rollback)
 * doesn't send them, so default to empty rather than crash building the tree.
 */
const sidebarOf = (data: Partial<IProject>): Pick<IProject, 'hubFolders' | 'sidebarResources'> => ({
  hubFolders: data.hubFolders ?? [],
  sidebarResources: data.sidebarResources ?? []
})

/** How long after a failed sidebar read it's tried once more. api() has already retried it by then. */
export const SIDEBAR_RETRY_MS = 10_000

const _useProjectProvider = () => {
  'use no memo'
  const { user } = useAuth()
  const { displayErrorMessage } = useApplicationStore()
  const { socket } = useSocket()
  const navigate = useNavigate()

  const [gettingData, setGettingData] = useState<boolean>(false)
  const [project, updateProject] = useState<IProject>(INITIAL_PROJECT_INFORMATION)
  const [tags, updateTags] = useState<ITag[]>([])
  const [search, setSearch] = useState<{
    open: boolean
    processPublicId?: string
    processName?: string
  }>({
    open: false,
    processPublicId: undefined,
    processName: undefined
  })
  const location = useLocation()
  const projectId = useMatch(`${HUB}/*`)?.params.projectId

  const openSearch = (processPublicId?: string, processName?: string) => {
    setSearch({
      open: true,
      processPublicId,
      processName
    })
  }

  const closeSearch = () => {
    setSearch({
      open: false,
      processPublicId: undefined,
      processName: undefined
    })
  }

  useEffect(() => {
    if (projectId) {
      if (projectId !== project.publicId && !gettingData) {
        setGettingData(true)
        updateProject((prev) => ({ ...prev, loading: true }))
        api
          .getProject(projectId)
          .then((response) => {
            if (response && response.data) {
              const projectData = response.data as IProject
              updateProject({ ...projectData, ...sidebarOf(projectData), loading: false })
            }
            return api.getUserProjectTags(projectId)
          })
          .then((response) => {
            if (response && response.data) {
              const tagsData = response.data as ITag[]
              updateTags(tagsData)
            }
            setGettingData(false)
          })
          .catch((error) => {
            setGettingData(false)
            if (!user) {
              setRedirectTo(location.pathname)
            }
            if (error.rawCode === 403) {
              navigate(`/nopermission?hub=${projectId}&type=hub&id=${projectId}`)
            } else {
              updateProject((prev) => ({ ...prev, loading: false }))
              displayErrorMessage(error)
            }
          })
      }
    } else {
      updateProject({ ...INITIAL_PROJECT_INFORMATION, loading: false })
    }
  }, [location])

  // The hub on screen, for callbacks created in an earlier render (a request that answers after the
  // user has switched hub still holds the old `project`).
  const currentHubId = useRef(project.publicId)
  useEffect(() => {
    currentHubId.current = project.publicId
  }, [project.publicId])

  // One sidebar read in flight per hub. What's asked for meanwhile waits, merged (null = the whole
  // sidebar), and is read once the read in flight has settled and its answer has been applied. So a
  // steady stream of events costs one read at a time, however slow the reads are, and every answer is
  // applied: a read only goes once the one before it has answered, so answers can't arrive out of
  // order. Keyed by hub, so a read for another hub never holds this hub's back.
  const readsInFlight = useRef(new Set<string>())
  const pendingReads = useRef(new Map<string, SidebarContainers>())
  // The project as last rendered, for reads that answer later: whether the viewer is a hub Owner, and
  // whether a container read fits the sidebar on screen.
  const latestProject = useRef(project)
  useEffect(() => {
    latestProject.current = project
  }, [project])
  // The hub whose last sidebar read failed (a 502 mid-deploy, or the network gone while the socket
  // stays up), so its sidebar may be out of date until a whole read succeeds. The first failure in a row
  // reads again once, a little later; after that it's read when the browser is back online or the tab
  // is shown again.
  const staleHub = useRef<string | null>(null)
  const retryTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  /**
   * Re-read `hubId`'s sidebar: all of it, or with `containers` just those containers' children (only
   * for a hub Owner, who sees every folder). While a read for the hub is in flight, this waits for it.
   */
  const readSidebar = (hubId: string, containers: SidebarContainers) => {
    if (!readsInFlight.current.has(hubId)) {
      sendSidebarRead(hubId, containers)
      return
    }
    const pending = pendingReads.current.get(hubId)
    pendingReads.current.set(hubId, pending === undefined ? containers : mergeContainers(pending, containers))
  }

  /**
   * Sends one sidebar read and applies its answer, taking only the sidebar's fields from it, so nothing
   * else on screen changes.
   *
   * Always server truth, with one catch: this tab skips the `resources_updated` events its own writes
   * cause, so a read that overlapped one of them (it may predate the write's commit) is followed by a
   * whole refresh once those writes have settled.
   */
  const sendSidebarRead = (hubId: string, wanted: SidebarContainers) => {
    readsInFlight.current.add(hubId)
    const mark = ownWriteMark(hubId)
    const applied = () => {
      if (readOverlappedOwnWrite(hubId, mark)) afterOwnWrites(hubId, () => refreshSidebar(hubId))
    }
    // The user may have moved to another hub while the read was in flight.
    const updateSidebar = (update: (prev: IProject) => IProject) =>
      updateProject((prev) => (prev.publicId !== hubId ? prev : update(prev)))
    // The hub's next read may go now: the one asked for meanwhile, if the hub is still on screen.
    const settle = () => {
      readsInFlight.current.delete(hubId)
      const next = pendingReads.current.get(hubId)
      pendingReads.current.delete(hubId)
      if (next !== undefined && hubId === currentHubId.current) sendSidebarRead(hubId, next)
    }

    const answer: Promise<void> =
      wanted === null
        ? api.getProject(hubId).then((response) => {
            if (!response?.data) return
            updateSidebar((prev) => ({ ...prev, ...sidebarOf(response.data) }))
            applied()
          })
        : api.getSidebarContainers(hubId, wanted).then((response) => {
            if (!response?.data) return
            const read = response.data
            // Something this read can't place (say, a folder it doesn't cover): read the whole sidebar
            // next.
            if (!applyContainerDiff(latestProject.current, wanted, read).consistent) {
              refreshSidebar(hubId)
              return
            }
            updateSidebar((prev) => ({ ...prev, ...applyContainerDiff(prev, wanted, read).sidebar }))
            applied()
          })
    const succeeded = () => {
      if (wanted === null && staleHub.current === hubId) staleHub.current = null
    }
    // A failed read changes nothing on screen, but the sidebar is read again (staleHub).
    const failed = () => {
      if (staleHub.current === hubId) return
      staleHub.current = hubId
      retryTimer.current = setTimeout(() => {
        if (staleHub.current === hubId) refreshSidebar(hubId)
      }, SIDEBAR_RETRY_MS)
    }
    answer.then(succeeded, failed).then(settle)
  }

  /**
   * Re-read the current hub's folders and resources and replace the sidebar with what the server has.
   * getProject asks for the hub without its member list (members load separately).
   * With `onlyForHubId`, does nothing unless that hub is still the one on screen.
   */
  const refreshSidebar = (onlyForHubId?: string) => {
    const hubId = currentHubId.current
    if (!hubId || (onlyForHubId !== undefined && onlyForHubId !== hubId)) return
    readSidebar(hubId, null)
  }

  /**
   * One update for a burst of `resources_updated` events on `hubId`. A hub Owner whose sidebar has every
   * container named re-reads just those; anyone else (who needs the server's permission check to know
   * what they see), or a burst that named none, re-reads the whole sidebar.
   */
  const onResourcesBurst = (hubId: string, containers: SidebarContainers) => {
    if (hubId !== currentHubId.current) return
    const current = latestProject.current
    const owner = current.publicId === hubId && current.role === ProjectRoles.OWNER
    readSidebar(hubId, owner && containers !== null && knowsContainers(current, containers) ? containers : null)
  }

  useEffect(() => {
    if (!project.publicId || project.publicId === '') return
    // Name the hub rather than trusting currentHubId: its effect and this one both run on a hub switch,
    // and an event arriving between them would otherwise refresh the wrong hub.
    const hubId = project.publicId
    const burst = createResourcesUpdatedBurst((containers) => onResourcesBurst(hubId, containers))

    // A reconnect may have missed events: read the whole sidebar.
    const leaveRoom = subscribeSocketRoom(socket, hubId, () => {
      burst.cancel()
      refreshSidebar(hubId)
    })

    const onResourcesUpdated = (data: IResourcesUpdatedSocket) => {
      // Our own sidebar writes are already on screen (helpers/realtimeOrigin).
      if (data.projectPublicId !== hubId || data.origin === realtimeOrigin()) return
      burst.push(containersOf(data))
    }

    // Back online, or back on this tab, after a read failed: read the whole sidebar.
    const readIfStale = () => {
      if (staleHub.current === hubId && document.visibilityState === 'visible') refreshSidebar(hubId)
    }

    socket.on('resources_updated', onResourcesUpdated)
    window.addEventListener('online', readIfStale)
    document.addEventListener('visibilitychange', readIfStale)

    return () => {
      leaveRoom()
      burst.cancel()
      clearTimeout(retryTimer.current)
      window.removeEventListener('online', readIfStale)
      document.removeEventListener('visibilitychange', readIfStale)
      socket.off('resources_updated', onResourcesUpdated)
    }
  }, [project.publicId])

  const projectRole = project.role
  const projectAdmin = projectRole === ProjectRoles.ADMIN || projectRole === ProjectRoles.OWNER
  const projectOwner = projectRole === ProjectRoles.OWNER

  return {
    project,
    updateProject,
    refreshSidebar,
    projectRole,
    projectAdmin,
    projectOwner,
    search,
    openSearch,
    closeSearch,
    tags
  }
}

export const ProjectProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  'use no memo'
  const value = _useProjectProvider()
  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>
}
